import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { changePassword } from '../../utils/auth';

export const PASSWORD_MIN_LENGTH = 8;

// Returns { score 0-4, label } — a rough indicator, the server enforces the real rules.
export function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= PASSWORD_MIN_LENGTH) score += 1;
  if (pw.length >= 12) score += 1;
  if (/\d/.test(pw) && /[a-zA-Z]/.test(pw)) score += 1;
  if (/[^a-zA-Z0-9]/.test(pw) || (/[a-z]/.test(pw) && /[A-Z]/.test(pw))) score += 1;
  return { score, label: ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'][score] };
}

export function passwordRuleError(pw) {
  if (pw.length < PASSWORD_MIN_LENGTH) return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`;
  if (!/\d/.test(pw)) return 'Password must contain at least one number.';
  return '';
}

export default function PasswordSection({ loggedInUser, onChanged }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState('');
  const type = showPasswords ? 'text' : 'password';
  const strength = passwordStrength(newPassword);
  const bar = ['bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-600'][strength.score];

  const submit = async () => {
    setError('');
    if (!currentPassword || !newPassword || !confirmPassword) return setError('All fields are required.');
    const ruleError = passwordRuleError(newPassword);
    if (ruleError) return setError(ruleError);
    if (newPassword !== confirmPassword) return setError('The new password and confirmation do not match.');
    try {
      await changePassword(loggedInUser, currentPassword, newPassword);
    } catch (err) {
      return setError(err.message || 'Could not change the password.');
    }
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    toast.success('Password changed.');
    if (onChanged) onChanged();
  };

  const input = 'border p-2 rounded text-black bg-white dark:bg-black dark:text-white';
  return (
    <div className="max-w-md">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        Rules: at least {PASSWORD_MIN_LENGTH} characters and at least one number.
      </p>
      <button type="button" onClick={() => setShowPasswords((v) => !v)}
        className="text-sm text-indigo-600 hover:text-indigo-800 mb-3">
        {showPasswords ? 'Hide passwords' : 'Show passwords'}
      </button>
      <div className="flex flex-col gap-3">
        <input type={type} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Current password" autoComplete="current-password" className={input} />
        <div>
          <input type={type} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password" autoComplete="new-password" className={`${input} w-full`} />
          {newPassword && (
            <div className="mt-1" aria-live="polite">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded">
                <div className={`h-1.5 rounded ${bar}`} style={{ width: `${(strength.score + 1) * 20}%` }} />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-300">{strength.label}</span>
            </div>
          )}
        </div>
        <input type={type} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password" autoComplete="new-password" className={input} />
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mt-2" role="alert">{error}</p>}
      <button onClick={submit} className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">
        Change password
      </button>
    </div>
  );
}
